import React, { ReactNode, useId, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, IconButton, Popover, Stack, Typography } from '@mui/material';
import type { PopoverOrigin } from '@mui/material/Popover';
import { alpha, useTheme } from '@mui/material/styles';

type Props = {
  ariaLabel: string;
  content: ReactNode;
  title?: ReactNode;
  maxWidth?: number;
  buttonSize?: number;
  iconSize?: number;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
};

const InfoHint: React.FC<Props> = ({
  ariaLabel,
  content,
  title,
  maxWidth = 320,
  buttonSize = 24,
  iconSize = 15,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
}) => {
  const theme = useTheme();
  const popoverId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl((current) => (current ? null : event.currentTarget));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-describedby={open ? popoverId : undefined}
        onClick={handleToggle}
        sx={{
          width: buttonSize,
          height: buttonSize,
          ml: 0.25,
          border: `1px solid ${alpha(
            theme.palette.primary.main,
            theme.palette.mode === 'dark' ? 0.4 : 0.18
          )}`,
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.mode === 'dark' ? 0.16 : 0.07
          ),
          color:
            theme.palette.mode === 'dark'
              ? alpha('#dbeafe', 0.95)
              : theme.palette.primary.dark,
          '&:hover': {
            backgroundColor: alpha(
              theme.palette.primary.main,
              theme.palette.mode === 'dark' ? 0.22 : 0.12
            ),
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: iconSize }} />
      </IconButton>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              maxWidth,
              borderRadius: 2.5,
              border: `1px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#dbe5f2'}`,
              background:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(13, 18, 33, 0.96), rgba(17, 24, 39, 0.93))'
                  : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(246, 250, 255, 0.96))',
              backdropFilter: 'blur(14px)',
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 18px 40px rgba(0, 0, 0, 0.34)'
                  : '0 18px 40px rgba(15, 23, 42, 0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
          }}
        />
        <Stack spacing={0.75} sx={{ p: 1.5 }}>
          {title ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              {title}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {content}
          </Typography>
        </Stack>
      </Popover>
    </>
  );
};

export default InfoHint;
